import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedMeshGradient — a subtle, classy moving gradient backdrop using NEXXIS palette.
 * Pure CSS animation, paused (via .is-paused) once it scrolls out of view so the
 * heavy blurred-blob repaints don't run while the hero is off-screen.
 */
export const AnimatedMeshGradient = ({
  className = "",
  // intensity controls overall opacity of the mesh
  intensity = 1,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`nexxis-mesh ${visible ? "" : "is-paused"} ${className}`}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
        opacity: intensity,
        WebkitMaskImage:
          "linear-gradient(to bottom, black 0%, black 72%, rgba(0,0,0,0.55) 90%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, black 0%, black 72%, rgba(0,0,0,0.55) 90%, transparent 100%)",
      }}
    >
      <div className="nm-blob nm-blob-1" />
      <div className="nm-blob nm-blob-2" />
      <div className="nm-blob nm-blob-3" />
      <div className="nm-blob nm-blob-4" />

      <style>{`
        .nexxis-mesh.is-paused .nm-blob { animation-play-state: paused; }
        .nexxis-mesh .nm-blob {
          position: absolute;
          width: 78vw;
          height: 78vw;
          max-width: 940px;
          max-height: 940px;
          border-radius: 50%;
          filter: blur(52px);
          /* static: rasterize the blur once into its own layer (cheap on scroll) */
          transform: translateZ(0);
          backface-visibility: hidden;
        }
        .nexxis-mesh .nm-blob-1 {
          background: radial-gradient(circle, rgba(13,95,94,0.88), transparent 72%);
          top: -22%;
          left: -10%;
        }
        .nexxis-mesh .nm-blob-2 {
          background: radial-gradient(circle, rgba(77,182,172,0.9), transparent 74%);
          top: 8%;
          right: -14%;
        }
        .nexxis-mesh .nm-blob-3 {
          background: radial-gradient(circle, rgba(190,229,224,0.95), transparent 72%);
          bottom: -26%;
          left: 30%;
        }
        .nexxis-mesh .nm-blob-4 {
          background: radial-gradient(circle, rgba(255,104,87,0.55), transparent 70%);
          top: 32%;
          left: 30%;
          width: 46vw;
          height: 46vw;
          max-width: 600px;
          max-height: 600px;
        }
        /* translate-only (no scale) so the blurred layer is cached and just
           moved — scaling a blur re-rasterizes it every frame (very costly). */
        @keyframes nmDrift1 {
          0%, 100% { transform: translate3d(0,0,0); }
          50% { transform: translate3d(30%, 22%, 0); }
        }
        @keyframes nmDrift2 {
          0%, 100% { transform: translate3d(0,0,0); }
          50% { transform: translate3d(-28%, 20%, 0); }
        }
        @keyframes nmDrift3 {
          0%, 100% { transform: translate3d(0,0,0); }
          50% { transform: translate3d(22%, -26%, 0); }
        }
        @keyframes nmDrift4 {
          0%, 100% { transform: translate3d(0,0,0); }
          50% { transform: translate3d(-20%, -18%, 0); }
        }
        /* dark mode tweak */
        [data-theme="dark"] .nexxis-mesh .nm-blob-1 {
          background: radial-gradient(circle, rgba(77,182,172,0.55), transparent 70%);
        }
        [data-theme="dark"] .nexxis-mesh .nm-blob-2 {
          background: radial-gradient(circle, rgba(190,229,224,0.50), transparent 72%);
        }
        [data-theme="dark"] .nexxis-mesh .nm-blob-3 {
          background: radial-gradient(circle, rgba(13,95,94,0.65), transparent 70%);
        }
        [data-theme="dark"] .nexxis-mesh .nm-blob-4 {
          background: radial-gradient(circle, rgba(255,125,109,0.66), transparent 70%);
          width: 50vw;
          height: 50vw;
          max-width: 640px;
          max-height: 640px;
        }
        @media (prefers-reduced-motion: reduce) {
          .nexxis-mesh .nm-blob { animation: none !important; }
        }
      `}</style>
    </div>
  );
};
